import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Duck-typed to match the StandardEvents.cartLinesUpdate contract observed
 * in assets/cart-drawer.js, assets/cart-discount.js, and assets/product-form.js:
 * a bubbling event carrying `.action` and a `.promise` that resolves to
 * `{ detail: { didError, cart } }`. The exact producing class could not be
 * confirmed in this environment; this mirrors the confirmed consumer shape
 * so existing listeners (e.g. cart-drawer-component) react normally.
 */
class NewayCartLinesUpdateEvent extends CustomEvent {
    /**
     * @param {Promise<{ detail: { didError: boolean, cart?: unknown, itemCount?: number } }>} promise
     */
    constructor(promise) {
        super(StandardEvents.cartLinesUpdate, { bubbles: true, cancelable: true });
        // Not 'add': that auto-opens the basket drawer over this one, hiding
        // the stepper the card has just flipped to. The footer's View cart
        // link is the way on to the basket.
        this.action = 'change';
        this.promise = promise;
    }
}

const cartRoot = () => window.Shopify?.routes?.root || '/';

/** @returns {Promise<Cart>} */
async function fetchCart() {
    const response = await fetch(`${cartRoot()}cart.js`, { headers: { Accept: 'application/json' } });
    return response.json();
}

/**
 * @typedef {{ key: string, variant_id: number, quantity: number }} CartLine
 * @typedef {{ item_count: number, total_price: number, currency: string, items: CartLine[] }} Cart
 */

/** In-memory cache for rendered card markup, keyed by ordered handle list. Never stores cart totals. */
const cardMarkupCache = new Map();

/**
 * Global featured-products drawer content controller. Sits inside the
 * shared <theme-drawer>/<dialog> shell (see snippets/neway-featured-products-drawer.liquid)
 * and owns: trigger delegation, Section-Rendering fetch + cache, add-to-cart
 * submission, and footer cart-summary refresh.
 */
class NewayFeaturedProductsDrawerComponent extends Component {
    /** @type {HTMLElement | null} */
    #activeTrigger = null;

    connectedCallback() {
        super.connectedCallback();

        document.addEventListener('click', this.#onDocumentClick);
        document.addEventListener('theme-drawer:open', this.#onDrawerOpen);
        document.addEventListener('theme-drawer:close', this.#onDrawerClose);
        this.addEventListener('submit', this.#onSubmit);
        this.addEventListener('click', this.#onStepClick);
        document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        document.removeEventListener('click', this.#onDocumentClick);
        document.removeEventListener('theme-drawer:open', this.#onDrawerOpen);
        document.removeEventListener('theme-drawer:close', this.#onDrawerClose);
        this.removeEventListener('submit', this.#onSubmit);
        this.removeEventListener('click', this.#onStepClick);
        document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    }

    /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
    get #themeDrawer() {
        return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
    }

    /**
     * Delegated click handler for every `[data-featured-products]` trigger on
     * the page (~25 tiles). Avoids attaching a listener per tile.
     * @param {MouseEvent} event
     */
    #onDocumentClick = (event) => {
        const trigger = /** @type {HTMLElement | null} */ (event.target instanceof Element
            ? event.target.closest('[data-featured-products]')
            : null);
        if (!trigger) return;

        event.preventDefault();
        this.#activeTrigger = trigger;
        this.#openWithHandles(trigger.getAttribute('data-featured-products') || '');
    };

    /**
     * @param {string} handlesAttr - Comma-separated, ordered product handles.
     */
    async #openWithHandles(handlesAttr) {
        const handles = handlesAttr
            .split(',')
            .map((h) => h.trim())
            .filter(Boolean);

        if (!handles.length) return;

        const cacheKey = handles.join('|');
        const list = this.querySelector('[data-neway-featured-list]');
        if (!list) return;

        if (cardMarkupCache.has(cacheKey)) {
            list.innerHTML = cardMarkupCache.get(cacheKey);
            this.#syncFromCart();
            this.#themeDrawer?.open();
            return;
        }

        try {
            // Liquid cannot read arbitrary custom query params (only the
            // requested URL's resource context, e.g. /products/{handle}, is
            // exposed). So each handle requires its own section-render fetch,
            // scoped to that product's own URL — not one fetch with a
            // comma-joined param. Order is preserved by Promise.all.
            const root = window.Shopify?.routes?.root || '/';
            const htmls = await Promise.all(
                handles.map((handle) =>
                    sectionRenderer.getSectionHTML(
                        'neway-featured-product-card',
                        true,
                        new URL(`${root}products/${handle}`, window.location.origin)
                    )
                )
            );

            const markup = htmls
                .map((html) => {
                    const doc = new DOMParser().parseFromString(html, 'text/html');
                    return doc.querySelector('[data-neway-featured-card-wrapper]')?.innerHTML ?? '';
                })
                .join('');

            cardMarkupCache.set(cacheKey, markup);
            list.innerHTML = markup;
            this.#syncFromCart();
            this.#themeDrawer?.open();
        } catch (error) {
            if (error?.name !== 'AbortError') console.warn('[neway-featured-products-drawer] Fetch failed:', error);
            this.#announce('Sorry, featured products could not be loaded.');
        }
    }

    #onDrawerOpen = () => {
        this.#activeTrigger?.setAttribute('aria-expanded', 'true');
    };

    #onDrawerClose = () => {
        this.#activeTrigger?.setAttribute('aria-expanded', 'false');
        this.#activeTrigger = null;
    };

    /**
     * Handles add-to-cart submissions for any compact card form rendered
     * inside this drawer (event delegation — one listener for all cards).
     * @param {SubmitEvent} event
     */
    #onSubmit = async (event) => {
        const form = /** @type {HTMLFormElement | null} */ (event.target);
        if (!(form instanceof HTMLFormElement) || !form.matches('[data-neway-featured-add-to-cart]')) return;

        event.preventDefault();

        const buy = form.closest('[data-neway-featured-buy]');
        const submitButton = form.querySelector('.neway-featured-card__submit');
        const idInput = form.querySelector('input[name="id"]');

        if (!(buy instanceof HTMLElement) || !(submitButton instanceof HTMLButtonElement) || !(idInput instanceof HTMLInputElement)) return;

        // Show the result straight away (review, 29 Sep 2026: the add took
        // most of a second behind a wait cursor): the stepper at 1, its
        // buttons live once the basket line comes back
        const stepButtons = buy.querySelectorAll('[data-neway-featured-step]');
        const count = buy.querySelector('[data-neway-featured-count]');
        submitButton.disabled = true;
        stepButtons.forEach((button) => (button.disabled = true));
        buy.toggleAttribute('data-in-basket', true);
        buy.dataset.quantity = '1';
        if (count) count.textContent = '1';
        // The button is gone; keep focus on the stepper that replaced it
        buy.querySelector('[data-neway-featured-step="1"]')?.focus();

        const ok = await this.#updateCart(buy, 'add', `${cartRoot()}cart/add.js`, { id: idInput.value, quantity: 1 });

        submitButton.disabled = false;
        stepButtons.forEach((button) => (button.disabled = false));

        if (!ok) {
            buy.toggleAttribute('data-in-basket', false);
            buy.dataset.quantity = '0';
            submitButton.focus();
            return;
        }
        this.#announce(`${form.dataset.productTitle || 'Item'} added to basket.`);
    };

    /**
     * Stepper −/+ : changes this variant's basket line by one. At zero the
     * line is removed and the card flips back to Add to basket.
     * @param {MouseEvent} event
     */
    #onStepClick = async (event) => {
        const stepButton = event.target instanceof Element ? event.target.closest('[data-neway-featured-step]') : null;
        const buy = stepButton?.closest('[data-neway-featured-buy]');
        if (!(stepButton instanceof HTMLButtonElement) || !(buy instanceof HTMLElement)) return;

        const lineKey = buy.dataset.lineKey;
        if (!lineKey) return;

        const quantity = Math.max(0, Number(buy.dataset.quantity || 0) + Number(stepButton.dataset.newayFeaturedStep));
        const stepButtons = buy.querySelectorAll('[data-neway-featured-step]');
        stepButtons.forEach((button) => (button.disabled = true));

        const ok = await this.#updateCart(buy, 'change', `${cartRoot()}cart/change.js`, { id: lineKey, quantity });

        stepButtons.forEach((button) => (button.disabled = false));

        if (!ok) return;
        if (quantity === 0) {
            this.#announce('Removed from basket.');
            buy.querySelector('.neway-featured-card__submit')?.focus();
        } else {
            this.#announce(`${quantity} in basket.`);
            stepButton.focus();
        }
    };

    /**
     * Posts a basket change, then refreshes every card and the footer from
     * the resulting cart. Tells the rest of the theme through the standard
     * cart event so the header count and basket drawer follow.
     * @param {HTMLElement} buy - The card's [data-neway-featured-buy] wrapper, for errors.
     * @param {'add' | 'change'} action
     * @param {string} url
     * @param {{ id: string, quantity: number }} body
     * @returns {Promise<boolean>} Whether the change went through.
     */
    async #updateCart(buy, action, url, body) {
        const errorEl = buy.querySelector('.neway-featured-card__error');
        errorEl?.setAttribute('hidden', '');

        const deferred = {};
        const promise = new Promise((resolve) => {
            deferred.resolve = resolve;
        });
        this.dispatchEvent(new NewayCartLinesUpdateEvent(promise));

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(body),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.description || data?.message || 'Could not update your basket.');
            }

            // add.js returns the line, not the cart: the card takes its key
            // and quantity now, and the whole basket (footer, header count,
            // drawer) follows in the background
            if (action === 'add') {
                buy.dataset.lineKey = data.key;
                buy.dataset.quantity = String(data.quantity);
                const count = buy.querySelector('[data-neway-featured-count]');
                if (count) count.textContent = String(data.quantity);
                fetchCart()
                    .then((cart) => {
                        deferred.resolve({ detail: { didError: false, cart, itemCount: cart.item_count } });
                        this.#applyCart(cart);
                    })
                    .catch(() => deferred.resolve({ detail: { didError: true } }));
                return true;
            }

            const cart = data;
            deferred.resolve({ detail: { didError: false, cart, itemCount: cart.item_count } });
            this.#applyCart(cart);
            return true;
        } catch (error) {
            deferred.resolve({ detail: { didError: true } });
            if (errorEl) {
                errorEl.textContent = error instanceof Error ? error.message : 'Could not update your basket.';
                errorEl.removeAttribute('hidden');
            }
            this.#announce('There was a problem updating your basket.');
            return false;
        }
    }

    /** Reads the basket when the drawer's cards are (re)inserted. */
    async #syncFromCart() {
        try {
            this.#applyCart(await fetchCart());
        } catch (error) {
            console.warn('[neway-featured-products-drawer] Cart fetch failed:', error);
        }
    }

    /**
     * Flips each card between Add to basket and the stepper to match the
     * basket, and refreshes the footer.
     * @param {Cart} cart
     */
    #applyCart(cart) {
        for (const buy of this.querySelectorAll('[data-neway-featured-buy]')) {
            if (!(buy instanceof HTMLElement)) continue;

            const line = cart.items.find((item) => String(item.variant_id) === buy.dataset.variantId);
            const count = buy.querySelector('[data-neway-featured-count]');

            buy.toggleAttribute('data-in-basket', Boolean(line));
            buy.dataset.quantity = String(line?.quantity ?? 0);
            if (line) buy.dataset.lineKey = line.key;
            else delete buy.dataset.lineKey;
            if (count && line) count.textContent = String(line.quantity);
        }

        this.#updateFooter(cart);
    }

    /**
     * Refreshes this drawer's own sticky footer after a confirmed successful
     * add. Never calculates the subtotal client-side — always reads it from
     * the cart response.
     * @param {{ item_count: number, total_price: number, currency: string }} cart
     */
    #updateFooter(cart) {
        const countEl = this.querySelector('[data-neway-featured-item-count]');
        const subtotalEl = this.querySelector('[data-neway-featured-subtotal]');
        if (countEl) {
            const n = cart.item_count;
            const one = new Intl.PluralRules(document.documentElement.lang || undefined).select(n) === 'one';
            countEl.textContent = one
                ? countEl.dataset.one ?? String(n)
                : (countEl.dataset.other ?? '[count]').replace('[count]', String(n));
        }
        if (subtotalEl) {
            // TODO: swap for the theme's confirmed money-formatting helper
            // (assets/money-formatting.js) once its export is verified — this
            // Intl fallback avoids inventing a bespoke formatter but may not
            // exactly match the store's configured money_format string.
            try {
                subtotalEl.textContent = new Intl.NumberFormat(document.documentElement.lang || undefined, {
                    style: 'currency',
                    currency: cart.currency,
                }).format(cart.total_price / 100);
            } catch {
                subtotalEl.textContent = String(cart.total_price / 100);
            }
        }
    }

    /**
     * @param {SubmitEvent} event - unused, kept for symmetry with other handlers.
     */
    #onCartLinesUpdate = () => {
        // Other components (header cart bubble, main cart drawer) handle their
        // own refresh from this same event; this drawer only needs its footer
        // refreshed, which #onSubmit already does directly from the confirmed
        // cart response to avoid a second, redundant fetch here.
    };

    /** @param {string} message */
    #announce(message) {
        const status = this.querySelector('[data-neway-featured-status]');
        if (status) status.textContent = message;
    }
}

if (!customElements.get('neway-featured-products-drawer-component')) {
    customElements.define('neway-featured-products-drawer-component', NewayFeaturedProductsDrawerComponent);
}